import React, { useState } from "react";
import Image from "next/image";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import httpClient from "@/api/http-client";
import endpoints from "@/api/endpoints";
import { phoneRegExp } from "@/utils/regex";
import styles from "../../styles/shap_of_you.module.scss";
import WelcomeScreenImg from "../../assets/images/WelcomeScreen_dek.png";
import SOYLockUp from "../../assets/images/SOYLockUp.png";
import { LoginScreenProps } from "./types";

const LoginScreen: React.FC<LoginScreenProps> = ({
  navigateToStep,
  authData,
  logStep,
}) => {
  const [sendOtpLoading, setSendOtpLoading] = useState(false);

  // Login Formik
  const loginFormik = useFormik({
    initialValues: {
      mobile: authData.mobileNumber || "",
    },
    validationSchema: Yup.object({
      mobile: Yup.string()
        .matches(phoneRegExp, "Mobile number is not valid")
        .test(
          "len",
          "Mobile number needs to be exactly 10 digits",
          (val: any) => val?.toString().length === 10
        )
        .required("Mobile is required"),
    }),
    onSubmit: async (values) => {
      setSendOtpLoading(true);
      const apiEndpoint = endpoints.auth.loginSendOtp();
      const requestPayload = { mobile: values.mobile };

      try {
        const { data } = await httpClient.post(apiEndpoint, requestPayload);

        // Log successful OTP send (non-blocking)
        if (logStep) {
          logStep("signup_otp_sent", {
            api_endpoint: apiEndpoint,
            request_payload: requestPayload,
            response_status: 200,
            response_payload: { otp_uuid: data.otp_uuid },
            success_message: data.message,
          });
        }

        // Success - mobile exists, user can login
        authData.setOtpUid(data.otp_uuid);
        authData.setMobileNumber(values.mobile);
        authData.setAuthFlow("login");

        toast(data.message, {
          type: "success",
          toastId: "auth-otp-success",
        });

        navigateToStep("verifyOtp");
      } catch (error: any) {
        // Log API error (non-blocking)
        if (logStep) {
          logStep("signup_otp_sent", {
            api_endpoint: apiEndpoint,
            request_payload: requestPayload,
            response_status: error.response?.status || 500,
            error_message:
              error.response?.data?.message || "Failed to send OTP",
            error_code: error.response?.status?.toString() || "500",
          });
        }

        // Check if error is "Mobile number does not exist"
        if (
          error.response?.status === 400 &&
          error.response?.data?.message === "Mobile number does not exist"
        ) {
          // Mobile doesn't exist - navigate to signup
          authData.setMobileNumber(values.mobile);
          authData.setAuthFlow("register");
          authData.setRegisterData({
            ...authData.registerData,
            mobile: values.mobile,
          });

          toast("Mobile number not registered. Please sign up.", {
            type: "info",
            toastId: "auth-signup-required",
          });

          navigateToStep("signup");
        } else {
          // Other errors
          if (error.response?.data?.message) {
            toast(error.response.data.message, {
              type: "error",
              toastId: "auth-otp-error",
            });
          } else {
            toast("Something went wrong! Try again!", {
              type: "error",
              toastId: "auth-otp-error",
            });
          }
        }
      }
      setSendOtpLoading(false);
    },
  });

  return (
    <div className="d-none d-md-block w-100">
      <div className={styles.Login_face_desktop}>
        <div className={styles.bgImage}>
          <Image src={WelcomeScreenImg} alt="Welcome Screen" />
        </div>
        <div className={styles.shapeOfYou}>
          <div className="container">
            <div className="row">
              <div className="col-md-7">
                <div className={styles.content}>
                  <div className={styles.title}>
                    <Image src={SOYLockUp} alt="Shape of You Logo" />
                  </div>
                </div>
              </div>
              <div className="col-md-5">
                <div className={styles.Login_container_form}>
                  <div className={styles.card}>
                    <h1 className={styles.title_m}>Login</h1>
                    <p className={styles.subtitle}>Enter your phone number</p>

                    <form onSubmit={loginFormik.handleSubmit}>
                      <div className={styles.inputBox}>
                        <svg
                          className={styles.icon}
                          width="20"
                          height="20"
                          viewBox="0 0 36 36"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M7.5 6H13.5L16.5 13.5L12.75 15.75C14.3564 19.0073 16.9927 21.6436 20.25 23.25L22.5 19.5L30 22.5V28.5C30 29.2956 29.6839 30.0587 29.1213 30.6213C28.5587 31.1839 27.7956 31.5 27 31.5C21.1489 31.1444 15.6302 28.6597 11.4852 24.5148C7.34026 20.3698 4.85557 14.8511 4.5 9C4.5 8.20435 4.81607 7.44129 5.37868 6.87868C5.94129 6.31607 6.70435 6 7.5 6Z"
                            stroke="#262121"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>

                        <input
                          type="text"
                          placeholder=" Phone number"
                          className={styles.input}
                          maxLength={10}
                          value={loginFormik.values.mobile}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            loginFormik.setFieldValue("mobile", value);
                          }}
                          onBlur={loginFormik.handleBlur}
                        />
                      </div>

                      {loginFormik.touched.mobile &&
                      loginFormik.errors.mobile ? (
                        <p
                          className="text-danger"
                          style={{
                            display: "block",
                            marginTop: "0px",
                            textAlign: "left",
                          }}
                        >
                          {loginFormik.errors.mobile}
                        </p>
                      ) : null}

                      <button
                        className={styles.button}
                        type="submit"
                        disabled={
                          sendOtpLoading ||
                          !loginFormik.isValid ||
                          !loginFormik.values.mobile ||
                          loginFormik.values.mobile.length !== 10
                        }
                        style={
                          sendOtpLoading ||
                          !loginFormik.isValid ||
                          !loginFormik.values.mobile ||
                          loginFormik.values.mobile.length !== 10
                            ? {
                                background: "#d3d3d3",
                                color: "#888",
                                cursor: "not-allowed",
                                opacity: 0.6,
                              }
                            : {
                                background: "#C9302C",
                                color: "white",
                                cursor: "pointer",
                              }
                        }
                      >
                        {sendOtpLoading ? "Sending..." : "Proceed"}{" "}
                        <svg
                          width="25"
                          height="17"
                          viewBox="0 0 30 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M0.000209928 11.0458L0 8.04596H24.2579L18.3333 2.12131L20.4546 0L30.0006 9.54596L20.4546 19.092L18.3333 16.9705L24.258 11.046L0.000209928 11.0458Z"
                            fill="currentcolor"
                          />
                        </svg>
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
